/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * bishop/events/v1/api.proto
 * OpenAPI spec version: version not set
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import * as axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type {
  BatchGetRunExecutionTimesParams,
  ListRunEventsParams,
  RpcStatus,
  V1BatchGetRunExecutionTimesResponse,
  V1ListRunEventsResponse,
} from '../../models';

/**
 * This RPC retrieves a paginated list of all events that occurred during
a specific automation run. By default events are returned in chronological
order (oldest first); set order_by="time desc" for newest-first.

Use the filter parameter to narrow results by specific criteria such as
node_kind for execution journal events.

The response includes both high-level run state changes (starts, completions,
failures) and detailed execution steps (node executions, execution patches,
detailed data flows in each expression, etc).
 * @summary List events for a given automation run.
 */
export const listRunEvents = (
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params?: ListRunEventsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ListRunEventsResponse>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/runs/${run}/events`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getListRunEventsQueryKey = (
  organization?: string,
  workspace?: string,
  automation?: string,
  run?: string,
  params?: ListRunEventsParams,
) => {
  return [
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/runs/${run}/events`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListRunEventsQueryOptions = <
  TData = Awaited<ReturnType<typeof listRunEvents>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params?: ListRunEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listRunEvents>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListRunEventsQueryKey(organization, workspace, automation, run, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRunEvents>>> = ({
    signal,
  }) =>
    listRunEvents(organization, workspace, automation, run, params, {
      signal,
      ...axiosOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(organization && workspace && automation && run),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listRunEvents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRunEventsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listRunEvents>>
>;
export type ListRunEventsQueryError = AxiosError<RpcStatus>;

export function useListRunEvents<
  TData = Awaited<ReturnType<typeof listRunEvents>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params: undefined | ListRunEventsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listRunEvents>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunEvents>>,
          TError,
          Awaited<ReturnType<typeof listRunEvents>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListRunEvents<
  TData = Awaited<ReturnType<typeof listRunEvents>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params?: ListRunEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listRunEvents>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunEvents>>,
          TError,
          Awaited<ReturnType<typeof listRunEvents>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListRunEvents<
  TData = Awaited<ReturnType<typeof listRunEvents>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params?: ListRunEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listRunEvents>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List events for a given automation run.
 */

export function useListRunEvents<
  TData = Awaited<ReturnType<typeof listRunEvents>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  automation: string,
  run: string,
  params?: ListRunEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listRunEvents>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListRunEventsQueryOptions(
    organization,
    workspace,
    automation,
    run,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * For each run, this RPC reads all state-transition events from InfluxDB
and computes:
  execution_duration = total_duration - exception_duration

where exception_duration is the cumulative time the run spent in the
AwaitingGuidance state (waiting for exception resolution) across all
exception cycles. All runs must belong to the same workspace.
 * @summary Compute the net execution time for multiple automation runs.
 */
export const batchGetRunExecutionTimes = (
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1BatchGetRunExecutionTimesResponse>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/runs/executionTimes:batchGet`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getBatchGetRunExecutionTimesQueryKey = (
  organization?: string,
  workspace?: string,
  params?: BatchGetRunExecutionTimesParams,
) => {
  return [
    `/api/v1/organizations/${organization}/workspaces/${workspace}/runs/executionTimes:batchGet`,
    ...(params ? [params] : []),
  ] as const;
};

export const getBatchGetRunExecutionTimesQueryOptions = <
  TData = Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getBatchGetRunExecutionTimesQueryKey(organization, workspace, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof batchGetRunExecutionTimes>>
  > = ({ signal }) =>
    batchGetRunExecutionTimes(organization, workspace, params, {
      signal,
      ...axiosOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(organization && workspace),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BatchGetRunExecutionTimesQueryResult = NonNullable<
  Awaited<ReturnType<typeof batchGetRunExecutionTimes>>
>;
export type BatchGetRunExecutionTimesQueryError = AxiosError<RpcStatus>;

export function useBatchGetRunExecutionTimes<
  TData = Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
          TError,
          Awaited<ReturnType<typeof batchGetRunExecutionTimes>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useBatchGetRunExecutionTimes<
  TData = Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
          TError,
          Awaited<ReturnType<typeof batchGetRunExecutionTimes>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useBatchGetRunExecutionTimes<
  TData = Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Compute the net execution time for multiple automation runs.
 */

export function useBatchGetRunExecutionTimes<
  TData = Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: BatchGetRunExecutionTimesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof batchGetRunExecutionTimes>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getBatchGetRunExecutionTimesQueryOptions(
    organization,
    workspace,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
